import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER} from 'ui-library/Colors';
import ErrorMessage from 'ui-library/ErrorMessage';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';

import {PivotTransform} from '../../RunSchemaSpecConstants';
import {
  CSVTextAreaInput,
  getColumnNamesFromInput,
  PivotFormErrors,
  updateTransformationProperties,
} from './TransformFormUtils';

interface PivotFormProps {
  transform: PivotTransform;
  onChange: (transform: PivotTransform) => void;
  formErrors: PivotFormErrors;
}

export default function PivotForm(props: PivotFormProps): JSX.Element {
  return (
    <>
      <Row className={css(styles.columnNamesContainer)}>
        <Col md={12}>
          <FieldLabel label="Index (row) column name(s)" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <CSVTextAreaInput
            value={props.transform.transformationProperties?.indexColumns}
            placeholder="Sample ID, Concentration"
            onChange={(value) => {
              props.onChange(updateTransformationProperties(props.transform, 'indexColumns', value));
            }}
            parseCSVInputFn={getColumnNamesFromInput}
            hasError={props.formErrors.indexColumns}
          />
          {props.formErrors.indexColumns ? <ErrorMessage error="Index columns are required" /> : null}
          <div className={css(styles.description)}>
            Enter a list of column name(s), separated by comma, to use as the index for the pivot. Each unique
            value in these columns will be converted into a row.
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label="Key column name"
            onChange={(e) => {
              props.onChange(updateTransformationProperties(props.transform, 'keysColumn', e.target.value));
            }}
            labelStyle={FieldLabel.STYLES.LABEL}
            isRequired={true}
            value={props.transform.transformationProperties?.keysColumn}
            hasError={props.formErrors.keysColumn}
          />
          {props.formErrors.keysColumn ? <ErrorMessage error="Column name is required" /> : null}
          <div className={css(styles.description)}>
            Each unique value in this column will become column headers for the pivoted table.
          </div>
        </Col>
        <Col md={6}>
          <TextField
            label="Value column name"
            onChange={(e) => {
              props.onChange(updateTransformationProperties(props.transform, 'valuesColumn', e.target.value));
            }}
            labelStyle={FieldLabel.STYLES.LABEL}
            isRequired={true}
            value={props.transform.transformationProperties?.valuesColumn}
            hasError={props.formErrors.valuesColumn}
          />
          {props.formErrors.valuesColumn ? <ErrorMessage error="Column name is required" /> : null}
          <div className={css(styles.description)}>
            This column will populate the values for each column in the pivoted table.
          </div>
        </Col>
      </Row>
    </>
  );
}

const styles = StyleSheet.create({
  description: {
    fontSize: 13,
    lineHeight: '16px',
    color: GRAY_DARKER,
    paddingTop: '7px',
  },
  columnNamesContainer: {
    paddingBottom: '25px',
  },
});
